import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserProfileService, UserRole } from '../../modules/usermanagement/services/user-profile.service';
import { UserService } from '../../modules/usermanagement/services/user.service';
import { TokenService } from '../../services/token.service';

@Component({
  template: '<div>Redirecting...</div>'
})
export class RoleBasedRedirectComponent implements OnInit {

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
    private userService: UserService,
    private tokenService: TokenService
  ) { }

  ngOnInit(): void {
    this.redirectBasedOnRole();
  }

  private redirectBasedOnRole(): void {
    const existingProfile = this.userProfileService.getCurrentUserProfile();

    if (!existingProfile) {
      // No user profile found, try to load it
      this.loadUserProfileAndRedirect();
      return;
    }

    this.performRoleBasedRedirect(existingProfile);
  }

  private loadUserProfileAndRedirect(): void {
    // Extract email from token
    const token = this.tokenService.getToken();
    if (!token) {
      console.warn('No token found, redirecting to login');
      this.router.navigate(['/login']);
      return;
    }

    try {
      const tokenParts = token.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      const email = payload.email || payload.unique_name;

      if (!email) {
        console.warn('No email found in token, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      // Verify token is valid before making API call
      if (!this.tokenService.isTokenValid()) {
        console.warn('Token is not valid, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      // Add small delay to ensure JWT token is properly set and available for interceptor
      setTimeout(() => {
        // Load user profile from API
        this.userService.getUserProfileByEmail(email).subscribe({
          next: (viewProfile) => {
            this.userProfileService.setUserProfile(viewProfile);
            this.performRoleBasedRedirect(viewProfile);
          },
          error: (error) => {
            console.error('Error loading user profile:', error);
            // Don't immediately redirect to login on first error - might be temporary
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 1000);
          }
        });
      }, 100); // Small delay to ensure token is set
    } catch (error) {
      console.error('Error extracting email from token:', error);
      this.router.navigate(['/login']);
    }
  }

  private performRoleBasedRedirect(userProfile: any): void {

    const userRole = userProfile.roleName?.trim();

    // Helper function for case-insensitive role matching
    const roleMatches = (role: UserRole): boolean => {
      return role.toLowerCase() === userRole?.toLowerCase();
    };

    // Explicit role-based routing using enum values for consistency
    let defaultRoute: string;

    if (roleMatches(UserRole.SUPER_ADMIN)) {
      defaultRoute = '/clients';
    } else if (
      roleMatches(UserRole.CLIENT_ADMIN) ||
      roleMatches(UserRole.CUSTOMER_ADMIN) ||
      roleMatches(UserRole.USER) ||
      roleMatches(UserRole.INSTALLER)
    ) {
      defaultRoute = '/customers';
    } else if (
      roleMatches(UserRole.RETAIL_ADMIN) ||
      roleMatches(UserRole.RETAIL_MANAGER) ||
      roleMatches(UserRole.RETAIL_USER) ||
      roleMatches(UserRole.RETAIL_USER_WITH_CONTROL)
    ) {
      defaultRoute = '/locations';
    } else {
      console.warn(`Unknown role: "${userRole}". Falling back to menu-based routing.`);
      // Fallback to original menu-based approach
      const visibleMenuItems = this.userProfileService.getVisibleMenuItems();
      if (visibleMenuItems.length > 0) {
        defaultRoute = visibleMenuItems[0].route;
      } else {
        console.warn(`User ${userProfile.roleName} has no accessible menu items, clearing profile`);
        this.userProfileService.clearUserProfile();
        this.router.navigate(['/login']);
        return;
      }
    }

    // Verify the user actually has access to this route before redirecting
    if (this.userProfileService.hasRouteAccess(defaultRoute)) {
      this.router.navigate([defaultRoute]);
    } else {
      // Fallback to first available menu item
      const visibleMenuItems = this.userProfileService.getVisibleMenuItems();
      if (visibleMenuItems.length > 0) {
        this.router.navigate([visibleMenuItems[0].route]);
      } else {
        this.userProfileService.clearUserProfile();
        this.router.navigate(['/login']);
      }
    }
  }
}